import { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form } from "reactstrap";
import { useMutation } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";

import { APIMS } from "../../../../../../config/apims";
import { APISD } from "../../../../../../config/apisd";
import ToastError from "../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../NotificationToast/ToastSuccess";

import "bulma/css/bulma.css";
import "../../../../../../index.css";

const formStyles = `
  .module-form-grid-one {
    grid-template-columns: 1fr;
  }

  .matpel-type-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .matpel-type-option {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
    padding: 14px 15px;
    margin: 0;
    border: 1px solid #dce3ef;
    border-radius: 12px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  }

  .matpel-type-option.active {
    border-color: #5576ed;
    background: #f5f7ff;
    box-shadow: 0 0 0 2px rgba(85, 118, 237, 0.08);
  }

  .matpel-type-option input {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin: 0;
    accent-color: #4169e1;
    cursor: pointer;
  }

  .matpel-type-option-title {
    display: block;
    color: #202b45;
    font-size: 13.5px;
    font-weight: 700;
  }

  .matpel-type-option-description {
    display: block;
    margin-top: 2px;
    color: #7d879b;
    font-size: 11.5px;
    line-height: 1.4;
  }

  @media (max-width: 620px) {
    .matpel-type-options {
      grid-template-columns: 1fr;
    }
  }
`;

export default function UpdateMataPelajaran(props) {
  const token = localStorage.getItem("token");
  const idUser = localStorage
    .getItem("id_admin")
    ?.replace(/[|&;$%@"<>()+,]/g, "");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingBranch, setLoadingBranch] = useState(false);
  const [branches, setBranches] = useState([]);

  const [form, setForm] = useState({
    ysb_branch_id: "",
    name_matpel: "",
    matpel_type: 0,
  });

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  useEffect(() => {
    if (!props?.dataUpdate) {
      return;
    }

    setForm({
      ysb_branch_id: props.dataUpdate?.ysb_branch_id || "",
      name_matpel: props.dataUpdate?.name_matpel || "",
      matpel_type: Number(props.dataUpdate?.matpel_type ?? 0),
    });
  }, [props?.dataUpdate]);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranch(true);

      try {
        const response = await APIMS.get(
          "/api/branches?page=1&limit=1000&ascending=1&search=",
          fetchParams
        );

        setBranches(response?.data?.data || []);
      } catch (error) {
        setBranches([]);

        ToastError.fire({
          icon: "error",
          title:
            error?.response?.data?.message ||
            "Gagal mengambil data cabang",
        });
      } finally {
        setLoadingBranch(false);
      }
    };

    fetchBranches();
  }, [fetchParams]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleMatpelTypeChange = (value) => {
    setForm((previous) => ({
      ...previous,
      matpel_type: Number(value),
    }));
  };

  const formIsComplete =
    Boolean(form.ysb_branch_id) && Boolean(form.name_matpel.trim());

  const handleSubmit = useMutation(async (event) => {
    event.preventDefault();

    if (!formIsComplete || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await APISD.put(
        `/api/matpels/${props.id}`,
        {
          ysb_branch_id: form.ysb_branch_id,
          name_matpel: form.name_matpel.trim(),
          matpel_type: Number(form.matpel_type),
          user_id: idUser,
        },
        fetchParams
      );

      if (response?.status === 200 || response?.status === 201) {
        ToastSuccess.fire({
          icon: "success",
          title: response?.data?.message || "Data berhasil diperbarui",
        });

        await props.GetResponseData();
        props.onHide();
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memperbarui data mata pelajaran",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <>
      <style>{formStyles}</style>

      <Modal
        {...props}
        size="lg"
        centered
        backdrop="static"
        keyboard={!isSubmitting}
        dialogClassName="sims-modal-dialog"
        contentClassName="sims-modal-content"
        aria-labelledby="update-matpel-title"
      >
        <Form onSubmit={(event) => handleSubmit.mutate(event)}>
          <Modal.Header className="sims-modal-header">
            <div>
              <h5 id="update-matpel-title" className="sims-modal-title">
                Update Mata Pelajaran
              </h5>

            </div>

            <button
              type="button"
              className="sims-modal-close"
              onClick={props.onHide}
              disabled={isSubmitting}
              aria-label="Tutup modal"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Modal.Header>

          <Modal.Body className="sims-modal-body mt-1 mx-2">
            <div className="sims-form-grid sims-form-grid-one">
              <div className="sims-form-group">
                <label className="sims-form-label" htmlFor="update-branch">
                  Cabang <span>*</span>
                </label>

                <select
                  id="update-branch"
                  name="ysb_branch_id"
                  className="sims-form-select"
                  value={form.ysb_branch_id}
                  onChange={handleChange}
                  disabled={loadingBranch}
                  required
                >
                  <option value="" disabled>
                    {loadingBranch ? "Memuat data..." : "Pilih Cabang"}
                  </option>

                  {branches.map((branch, index) => (
                    <option
                      key={branch?.branch_code || index}
                      value={branch?.branch_code}
                    >
                      {branch?.branch_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sims-form-group">
                <label className="sims-form-label" htmlFor="update-name">
                  Nama Mata Pelajaran <span>*</span>
                </label>

                <input
                  id="update-name"
                  type="text"
                  name="name_matpel"
                  className="sims-form-input"
                  value={form.name_matpel}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />
              </div>

              <div className="sims-form-group">
                <label className="sims-form-label">
                  Tipe Mata Pelajaran <span>*</span>
                </label>

                <div className="matpel-type-options">
                  <label
                    className={`matpel-type-option ${
                      Number(form.matpel_type) === 0 ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="matpel_type"
                      value={0}
                      checked={Number(form.matpel_type) === 0}
                      onChange={() => handleMatpelTypeChange(0)}
                    />

                    <span>
                      <span className="matpel-type-option-title">
                        Intrakurikuler
                      </span>
                      <span className="matpel-type-option-description">
                        Mata pelajaran utama dalam pembelajaran.
                      </span>
                    </span>
                  </label>

                  <label
                    className={`matpel-type-option ${
                      Number(form.matpel_type) === 1 ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="matpel_type"
                      value={1}
                      checked={Number(form.matpel_type) === 1}
                      onChange={() => handleMatpelTypeChange(1)}
                    />

                    <span>
                      <span className="matpel-type-option-title">
                        Kokurikuler
                      </span>
                      <span className="matpel-type-option-description">
                        Kegiatan pendukung proses pembelajaran.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="sims-modal-footer">
            <button
              type="button"
              className="sims-modal-cancel"
              onClick={props.onHide}
              disabled={isSubmitting}
            >
              Batal
            </button>

            <button
              type="submit"
              className="sims-modal-submit"
              disabled={!formIsComplete || isSubmitting}
            >
              {/* {isSubmitting && <span className="sims-button-spinner" />}
              {isSubmitting ? "Menyimpan..." : "Perbarui"} */}

              {isSubmitting ? (
                <>
                  <span className="sims-button-spinner" />
                  <span>Memperbarui...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faFloppyDisk}
                  />
                  <span>Perbarui</span>
                </>
              )}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
